import { useState } from "react";
import { Link } from "react-router-dom";
import { Plane, BedDouble, Ticket, Wrench, Trash2, RefreshCw, CheckSquare, Plus, X, Eye, EyeOff, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import AddItemDialog from "./AddItemDialog";

const KIND_ICON = { flight: Plane, stay: BedDouble, event: Ticket, manual: Wrench };

export function fmtMinutes(m) {
  if (!m) return "0h";
  const h = Math.floor(m / 60), mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}
export function fmtCost(c) {
  if (c === null || c === undefined) return "—";
  return `$${Number(c).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
const fmtTime = (iso) => (iso && iso.includes("T") ? iso.slice(11, 16) : "");

function OfferPicker({ scenarioId, item, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  if (!item.offers?.length) return null;
  const pick = async (oid) => {
    if (oid === item.selected_offer_id && !item.manual_price) return;
    setBusy(true);
    try {
      await api.patch(`/scenarios/${scenarioId}/items/${item.id}`, { selected_offer_id: oid });
      onChanged();
    } catch { toast.error("Could not select offer"); } finally { setBusy(false); }
  };
  const visible = showAll ? item.offers : item.offers.slice(0, 3);
  return (
    <div className="mt-2 space-y-1">
      {visible.map((o) => {
        const sel = o.id === item.selected_offer_id && !item.manual_price;
        const sl = o.slices?.[0] || {};
        return (
          <button key={o.id} onClick={() => pick(o.id)} disabled={busy}
            className={`w-full flex items-center justify-between gap-2 px-2 py-1 text-[11px] font-mono border transition-colors ${
              sel ? "border-ink bg-sand-50" : "border-sand-200 text-sand-500 hover:border-sand-400"}`}
            data-testid={`offer-pick-${item.id}-${o.id}`}>
            <span className="truncate">{sel ? "●" : "○"} {o.airline} · {fmtTime(sl.departing_at)}→{fmtTime(sl.arriving_at)} · {sl.stops === 0 ? "nonstop" : `${sl.stops} stop`}</span>
            <span className="shrink-0 font-medium text-ink">${Math.round(o.total_amount)}</span>
          </button>
        );
      })}
      {item.offers.length > 3 && (
        <button onClick={() => setShowAll(!showAll)}
          className="w-full py-1 text-[10px] font-mono uppercase tracking-wide text-sand-500 hover:text-ink transition-colors"
          data-testid={`offers-toggle-${item.id}`}>
          {showAll ? "Show fewer" : `Show all ${item.offers.length} offers`}
        </button>
      )}
    </div>
  );
}

function ManualPrice({ scenarioId, item, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (val === "" || Number(val) < 0) return;
    setBusy(true);
    try {
      await api.patch(`/scenarios/${scenarioId}/items/${item.id}`, { est_cost: Number(val) });
      setEditing(false);
      onChanged();
    } catch { toast.error("Could not set price"); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="mt-1.5 flex items-center gap-1.5">
        <span className="font-mono text-[11px] text-sand-500">$</span>
        <input type="number" min="0" autoFocus value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="0 for miles"
          className="w-28 border border-sand-300 px-2 py-1 text-[11px] font-mono focus:outline-none focus:border-ink"
          data-testid={`manual-price-input-${item.id}`} />
        <button onClick={save} disabled={busy || val === ""}
          className="font-mono text-[10px] uppercase tracking-wide text-ember hover:underline disabled:opacity-40"
          data-testid={`manual-price-save-${item.id}`}>Set</button>
        <button onClick={() => setEditing(false)} className="font-mono text-[10px] uppercase tracking-wide text-sand-400 hover:text-ink">Cancel</button>
      </div>
    );
  }
  return (
    <button onClick={() => { setVal(item.manual_price && item.est_cost != null ? String(item.est_cost) : ""); setEditing(true); }}
      className="mt-1.5 font-mono text-[10px] uppercase tracking-wide text-sand-400 hover:text-ember transition-colors"
      data-testid={`manual-price-toggle-${item.id}`}>
      {item.manual_price ? "✎ Edit manual price" : "Set price manually · $0 = miles"}
    </button>
  );
}

function ItemRow({ scenario, item, onChanged, onEdit }) {
  const Icon = KIND_ICON[item.kind] || Wrench;
  const committed = scenario.status === "committed";
  const remove = async () => {
    try {
      await api.delete(`/scenarios/${scenario.id}/items/${item.id}`);
      onChanged();
    } catch { toast.error("Could not remove item"); }
  };
  const sub = [];
  if (item.kind === "flight") {
    sub.push(item.date);
    if (item.manual_price) sub.push("manual price");
    else if (item.selected_summary) sub.push(`${item.selected_summary.airline}${item.selected_summary.stops === 0 ? " · nonstop" : ""}`);
    else if (item.fetch_error) sub.push(item.fetch_error);
    else if (!item.fetched_at) sub.push("not priced yet");
  }
  if (item.kind === "stay") sub.push(`${item.check_in} → ${item.check_out} · ${item.nights} night${item.nights > 1 ? "s" : ""}${item.nightly_estimate ? ` @ $${item.nightly_estimate}` : ""}`);
  if (item.kind === "event") sub.push([item.date, item.location].filter(Boolean).join(" · "));
  if (item.kind === "manual" && item.travel_minutes) sub.push(`${fmtMinutes(item.travel_minutes)} travel`);

  return (
    <div className="py-3 border-b border-sand-100 group" data-testid={`scenario-item-${item.id}`}>
      <div className="grid grid-cols-[24px_1fr_auto] gap-2 items-start">
        <Icon size={14} className="mt-0.5 text-sand-400" />
        <button type="button" onClick={committed ? undefined : onEdit} disabled={committed}
          className="min-w-0 text-left cursor-pointer disabled:cursor-default hover:text-ember transition-colors"
          title={committed ? undefined : "Edit item"}
          data-testid={`item-edit-${item.id}`}>
          <p className="text-sm font-medium truncate">{item.label}</p>
          <p className="text-[11px] font-mono text-sand-500 truncate">{sub.join(" · ")}</p>
        </button>
        <div className="text-right flex items-start gap-1.5">
          <div>
            <p className="font-mono text-sm font-medium">{fmtCost(item.est_cost)}</p>
            {item.travel_minutes ? <p className="font-mono text-[10px] text-sand-400">{fmtMinutes(item.travel_minutes)}</p> : null}
          </div>
          {!committed && (
            <>
              <button onClick={onEdit} className="opacity-0 group-hover:opacity-100 p-1 text-sand-400 hover:text-ember transition-opacity"
                data-testid={`item-edit-btn-${item.id}`}>
                <Pencil size={12} />
              </button>
              <button onClick={remove} className="opacity-0 group-hover:opacity-100 p-1 text-sand-400 hover:text-error transition-opacity"
                data-testid={`item-delete-${item.id}`}>
                <X size={12} />
              </button>
            </>
          )}
        </div>
      </div>
      {!committed && item.kind === "flight" && (
        <>
          <OfferPicker scenarioId={scenario.id} item={item} onChanged={onChanged} />
          <ManualPrice scenarioId={scenario.id} item={item} onChanged={onChanged} />
        </>
      )}
    </div>
  );
}

export default function ScenarioCard({ scenario, color, onChanged, onHover, hidden, onToggleVisible }) {
  const [adding, setAdding] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [committing, setCommitting] = useState(false);
  const t = scenario.totals;
  const committed = scenario.status === "committed";
  const unfetched = scenario.items.filter((i) => i.kind === "flight" && !i.fetched_at).length;

  const fetchPrices = async (force = false) => {
    setFetching(true);
    try {
      const { data } = await api.post(`/scenarios/${scenario.id}/fetch${force ? "?force=true" : ""}`);
      toast.success(`Priced ${data.fetched_count} flight leg${data.fetched_count === 1 ? "" : "s"}`);
      onChanged();
    } catch { toast.error("Price fetch failed"); } finally { setFetching(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`/scenarios/${scenario.id}/commit`);
      toast.success(`Trip "${data.trip_name}" created — confirmed on your calendar`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally { setCommitting(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete scenario "${scenario.name}"?`)) return;
    try {
      await api.delete(`/scenarios/${scenario.id}`);
      onChanged();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div
      className={`bg-surface border-2 flex flex-col transition-opacity ${committed ? "border-sand-300 opacity-75" : "border-ink"}`}
      style={committed ? {} : { borderTopColor: color, borderTopWidth: 4 }}
      onMouseEnter={() => onHover(scenario.id)} onMouseLeave={() => onHover(null)}
      data-testid={`scenario-card-${scenario.id}`}>
      <div className="p-5 pb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 shrink-0 border-2 border-dashed" style={{ borderColor: color }} />
            <h3 className="font-display font-medium tracking-tight text-lg truncate" data-testid="scenario-name">{scenario.name}</h3>
          </div>
          <p className="label-tag mt-1">
            {t.start_date ? `${t.start_date} → ${t.end_date}` : "No dated items"}
            {committed && <span className="text-success ml-2">· Committed</span>}
          </p>
          {scenario.calendar_conflicts?.count > 0 && (
            <p className="mt-1.5 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-amber-100 text-amber-800"
              title={scenario.calendar_conflicts.sample.map((c) => `${c.date} · ${c.title}${c.calendar ? ` (${c.calendar})` : ""}`).join("\n")}
              data-testid={`scenario-conflicts-${scenario.id}`}>
              ⚠ {scenario.calendar_conflicts.count} calendar conflict{scenario.calendar_conflicts.count === 1 ? "" : "s"}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!committed && (
            <button onClick={() => onToggleVisible(scenario.id)} className="p-1.5 text-sand-400 hover:text-ink" title={hidden ? "Show on calendar" : "Hide from calendar"}
              data-testid={`scenario-toggle-${scenario.id}`}>
              {hidden ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          )}
          <button onClick={remove} className="p-1.5 text-sand-400 hover:text-error" data-testid={`scenario-delete-${scenario.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="px-5 flex-1">
        {scenario.items.length === 0 ? (
          <div className="border border-dashed border-sand-300 p-6 text-center label-tag">No items yet — add flights, stays, events</div>
        ) : (
          scenario.items.map((it) => <ItemRow key={it.id} scenario={scenario} item={it} onChanged={onChanged} onEdit={() => setEditItem(it)} />)
        )}
        {!committed && (
          <button onClick={() => setAdding(true)} className="mt-3 w-full border border-dashed border-sand-300 py-2 font-mono text-[11px] uppercase tracking-wide text-sand-500 hover:border-ink hover:text-ink transition-colors flex items-center justify-center gap-1.5"
            data-testid={`scenario-add-item-${scenario.id}`}>
            <Plus size={12} /> Add item
          </button>
        )}
      </div>

      <div className="p-5 mt-4 border-t-2 border-ink grid grid-cols-3 gap-3" data-testid={`scoreboard-${scenario.id}`}>
        <div>
          <p className="label-tag">Total cost</p>
          <p className="font-display font-medium text-2xl" data-testid="scoreboard-cost">{fmtCost(t.cost)}</p>
          {t.unpriced_items > 0 && <p className="font-mono text-[9px] text-amber-600">+{t.unpriced_items} unpriced</p>}
        </div>
        <div>
          <p className="label-tag">Travel time</p>
          <p className="font-display font-medium text-2xl" data-testid="scoreboard-time">{fmtMinutes(t.travel_minutes)}</p>
        </div>
        <div>
          <p className="label-tag">Flights</p>
          <p className="font-display font-medium text-2xl" data-testid="scoreboard-flights">{t.flights}</p>
        </div>
      </div>

      <div className="px-5 pb-5 flex gap-2">
        {committed ? (
          <Link to={`/trips/${scenario.trip_id}`} className="btn-swiss btn-primary flex-1 text-center" data-testid={`scenario-view-trip-${scenario.id}`}>
            View trip →
          </Link>
        ) : (
          <>
            <button onClick={() => fetchPrices(unfetched === 0)} disabled={fetching || scenario.items.every((i) => i.kind !== "flight")}
              className="btn-swiss btn-ghost border border-ink flex-1 disabled:opacity-40 flex items-center justify-center gap-1.5"
              data-testid={`scenario-fetch-${scenario.id}`}>
              {fetching ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              {unfetched > 0 ? `Price ${unfetched} leg${unfetched > 1 ? "s" : ""}` : "Re-price"}
            </button>
            <button onClick={commit} disabled={committing || scenario.items.length === 0}
              className="btn-swiss btn-primary flex-1 disabled:opacity-40 flex items-center justify-center gap-1.5"
              data-testid={`scenario-commit-${scenario.id}`}>
              {committing ? <Loader2 size={13} className="animate-spin" /> : <CheckSquare size={13} />}
              Commit
            </button>
          </>
        )}
      </div>

      {adding && <AddItemDialog scenarioId={scenario.id} onClose={(ok) => { setAdding(false); if (ok) onChanged(); }} />}
      {editItem && <AddItemDialog scenarioId={scenario.id} item={editItem} onClose={(ok) => { setEditItem(null); if (ok) onChanged(); }} />}
    </div>
  );
}
